// WaitlistDrawer.jsx
// Side drawer that hangs off the Calendar. Lets staff:
//   • Add a client to a waitlist for a target date/time/treatment
//   • See who's waiting (FIFO order)
//   • Promote a waitlist entry to a real appointment (+ optional cancel of
//     the existing booking they're shadowing)
//   • Send a manual "you're confirmed" SMS
//
// Auto-promote on cancel/reschedule is handled by the `waitlist-promote`
// Edge Function — see appointmentCancellation / appointmentReschedule
// callsites in elyon-core.jsx.
import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
  warm:"#f59e0b",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {
  width:"100%", padding:"10px 12px", background:C.light,
  border:`1px solid ${C.border}`, borderRadius:8, fontSize:13,
  color:C.text, fontFamily:F, outline:"none", boxSizing:"border-box",
};

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hh}:${String(m||0).padStart(2,"0")} ${h >= 12 ? "PM" : "AM"}`;
};

const fmtDate = (d) => {
  if (!d) return "";
  return new Date(d + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
};

export default function WaitlistDrawer({ open, onClose, services = [], providers = [], onPromoted, onAdded }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState("waiting"); // waiting | all | confirmed

  const load = async () => {
    setLoading(true);
    let q = supabase.from("waitlist_entries")
      .select("*")
      .order("created_at", { ascending: true });
    if (filter === "waiting") q = q.eq("status", "waiting");
    if (filter === "confirmed") q = q.eq("status", "confirmed");
    const { data } = await q;
    setEntries(data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (!open) return;
    load();
    const ch = supabase.channel("waitlist-drawer")
      .on("postgres_changes", { event:"*", schema:"public", table:"waitlist_entries" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filter]);

  if (!open) return null;

  const waitingCount = entries.filter(e => e.status === "waiting").length;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.35)",
          zIndex:1100, backdropFilter:"blur(2px)",
        }}
      />
      {/* Drawer panel */}
      <div style={{
        position:"fixed", top:0, right:0, bottom:0, width:"min(440px, 100vw)",
        background:C.surface, zIndex:1101, boxShadow:"-12px 0 32px rgba(0,0,0,0.18)",
        display:"flex", flexDirection:"column",
        animation:"slideIn 0.22s ease-out",
      }}>
        <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{padding:"18px 20px",borderBottom:`1px solid ${C.border}`,background:`linear-gradient(135deg, ${C.goldBg}, #fff)`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>⏳ Waitlist</div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.muted,padding:0,lineHeight:1}}>×</button>
          </div>
          <div style={{fontSize:12,color:C.muted}}>{waitingCount} waiting · auto-promotes when slots free up</div>
        </div>

        {/* Tabs + add */}
        <div style={{padding:"12px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:8,flex:1}}>
            {[["waiting","Waiting"],["confirmed","Confirmed"],["all","All"]].map(([v, l]) => (
              <button key={v} onClick={()=>setFilter(v)} style={{
                flex:1,padding:"6px 10px",fontSize:11,fontWeight:filter===v?800:600,
                color:filter===v?"#fff":C.muted,
                background:filter===v?`linear-gradient(135deg,${C.gold},${C.goldD})`:"none",
                borderRadius:6,border:"none",cursor:"pointer",fontFamily:F,
              }}>{l}</button>
            ))}
          </div>
          <button onClick={()=>setShowAdd(true)} style={{padding:"8px 14px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>+ Add</button>
        </div>

        {/* Entries list */}
        <div style={{flex:1,overflowY:"auto",padding:"12px"}}>
          {loading && <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>Loading...</div>}
          {!loading && entries.length === 0 && (
            <div style={{padding:"40px 20px",textAlign:"center"}}>
              <div style={{fontSize:36,opacity:0.3,marginBottom:8}}>⏳</div>
              <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:4}}>No one on the waitlist</div>
              <div style={{fontSize:11,color:C.muted}}>Add a client when they want a slot that's already booked.</div>
            </div>
          )}
          {!loading && entries.map(e => (
            <WaitlistCard key={e.id} entry={e} onChanged={load} onPromoted={onPromoted}/>
          ))}
        </div>
      </div>

      {showAdd && (
        <AddWaitlistModal
          services={services}
          providers={providers}
          onClose={()=>setShowAdd(false)}
          onSaved={()=>{ setShowAdd(false); load(); if (onAdded) onAdded(); }}
        />
      )}
    </>
  );
}

function WaitlistCard({ entry, onChanged, onPromoted }) {
  const [busy, setBusy] = useState(false);
  const isWaiting = entry.status === "waiting";
  const isConfirmed = entry.status === "confirmed";
  const isCancelled = entry.status === "cancelled";

  const statusBadge = isConfirmed
    ? { label:"✅ Confirmed", bg:C.greenL, color:C.green }
    : isCancelled
    ? { label:"❌ Cancelled", bg:C.redL, color:C.red }
    : { label:"⏳ Waiting", bg:C.goldBg, color:C.gold };

  const sendConfirmSms = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const treatment = entry.treatment_name || "your appointment";
      const dateStr = fmtDate(entry.target_date);
      const timeStr = entry.target_time ? ` at ${fmt12(entry.target_time)}` : "";
      const provStr = entry.provider_name ? ` with ${entry.provider_name.split(" ")[0]}` : "";
      const message = `Hi ${(entry.client_name||"").split(" ")[0]}! 🎉 A spot just opened up — your ${treatment} is confirmed for ${dateStr}${timeStr}${provStr}. See you then 💛`;
      const { error } = await supabase.functions.invoke("send-sms", {
        body: { to: entry.client_phone, message, sender: "system", source: "waitlist_confirm" }
      });
      if (error) throw error;
      await supabase.from("waitlist_entries").update({
        status: "confirmed",
        confirmed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq("id", entry.id);
      alert("✅ Confirmation SMS sent");
      if (onChanged) onChanged();
    } catch (e) {
      alert("SMS failed: " + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const promote = async () => {
    if (busy) return;
    if (!window.confirm(`Promote ${entry.client_name} to a real appointment for ${fmtDate(entry.target_date)}${entry.target_time ? " at "+fmt12(entry.target_time) : ""}?\n\nThis will create the booking and send a confirmation SMS.`)) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-promote", {
        body: { waitlist_id: entry.id, source: "manual" }
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Promote failed");
      if (onPromoted) onPromoted(data.appointment);
      if (onChanged) onChanged();
    } catch (e) {
      alert("Promote failed: " + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${entry.client_name} from the waitlist?`)) return;
    await supabase.from("waitlist_entries").update({
      status: "cancelled",
      updated_at: new Date().toISOString(),
    }).eq("id", entry.id);
    if (onChanged) onChanged();
  };

  return (
    <div style={{
      padding:"12px 14px", background:C.card,
      border:`1px solid ${isWaiting ? C.borderGold : C.border}`,
      borderLeft:`4px solid ${statusBadge.color}`,
      borderRadius:10, marginBottom:10,
      opacity: isCancelled ? 0.55 : 1,
    }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text}}>{entry.client_name}</div>
          <div style={{fontSize:11,color:C.muted}}>{entry.client_phone}</div>
        </div>
        <span style={{fontSize:9,padding:"3px 8px",background:statusBadge.bg,color:statusBadge.color,borderRadius:50,fontWeight:700}}>{statusBadge.label}</span>
      </div>

      <div style={{padding:"8px 10px",background:C.light,borderRadius:7,marginBottom:8,fontSize:12,color:C.text}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
          <span style={{fontSize:10,color:C.muted,minWidth:60}}>Treatment:</span>
          <strong>{entry.treatment_name || "Any"}</strong>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
          <span style={{fontSize:10,color:C.muted,minWidth:60}}>Wants:</span>
          <strong>{fmtDate(entry.target_date)}{entry.target_time ? ` · ${fmt12(entry.target_time)}` : ""}</strong>
        </div>
        {entry.provider_name && (
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
            <span style={{fontSize:10,color:C.muted,minWidth:60}}>Provider:</span>
            <strong>{entry.provider_name}</strong>
          </div>
        )}
        {entry.notes && (
          <div style={{fontSize:11,color:C.muted,fontStyle:"italic",marginTop:4,padding:"4px 0",borderTop:`1px solid ${C.border}`}}>{entry.notes}</div>
        )}
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:10,color:C.dim,marginBottom:8}}>
        <span>Added {new Date(entry.created_at).toLocaleString()}</span>
        {entry.linked_appointment_id && <span style={{color:C.purple,fontWeight:600}}>🔗 Shadowing appt #{entry.linked_appointment_id}</span>}
      </div>

      {isWaiting && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={promote} disabled={busy} style={{flex:1,padding:"7px 10px",background:`linear-gradient(135deg,${C.green},#1e6b3f)`,color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:F}}>✓ Promote → Booked</button>
          <button onClick={sendConfirmSms} disabled={busy} style={{padding:"7px 10px",background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:7,fontSize:11,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:F}}>📤 Send Confirm</button>
          <button onClick={remove} disabled={busy} style={{padding:"7px 10px",background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:7,fontSize:11,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:F}}>🗑</button>
        </div>
      )}
      {isConfirmed && entry.confirmed_at && (
        <div style={{fontSize:10,color:C.green,fontWeight:600,marginTop:4}}>✓ Confirmed {new Date(entry.confirmed_at).toLocaleString()}</div>
      )}
    </div>
  );
}

function AddWaitlistModal({ services, providers, onClose, onSaved }) {
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({ name:"", phone:"", email:"" });
  const [useNewClient, setUseNewClient] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [providerId, setProviderId] = useState("");
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split("T")[0]);
  const [targetTime, setTargetTime] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Live client search (paginated to avoid 1000-row cap)
  useEffect(() => {
    if (useNewClient) return;
    const q = clientSearch.trim();
    if (q.length < 2) { setClientResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const run = async () => {
      const { data } = await supabase.from("clients")
        .select("id,name,phone,email")
        .or(`name.ilike.%${q}%,phone.ilike.%${q}%,email.ilike.%${q}%`)
        .limit(8);
      if (!cancelled) {
        setClientResults(data || []);
        setSearching(false);
      }
    };
    const t = setTimeout(run, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clientSearch, useNewClient]);

  const canSave =
    (useNewClient ? (newClient.name && newClient.phone) : !!selectedClient) &&
    targetDate;

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    try {
      let client = selectedClient;
      // Create new client if needed.
      // ★ id is bigint NOT NULL with no sequence default on the clients
      //   table (legacy schema). Every other create-client path supplies an
      //   id with Date.now() — this waitlist flow used to skip it, which
      //   threw `null value in column "id" of relation "clients" violates
      //   not-null constraint` when staff added someone fresh to the list.
      if (useNewClient) {
        const { data: created, error: createErr } = await supabase.from("clients")
          .insert([{
            id: Date.now(),
            name: newClient.name,
            phone: newClient.phone,
            email: newClient.email || null,
            status: "active",
            allergies: "None",
            med: "No contraindications",
            spent: 0,
            visits: 0,
            noshow: 0,
            tags: ["Waitlist"],
            loyalty_pts: 0,
          }])
          .select().single();
        if (createErr) throw createErr;
        client = created;
      }

      const svc = services.find(s => String(s.id) === String(serviceId));
      const prov = providers.find(p => String(p.id) === String(providerId));
      const { data: { user } } = await supabase.auth.getUser();
      const { data: prof } = user
        ? await supabase.from("profiles").select("full_name").eq("id", user.id).single()
        : { data: null };

      const { data: inserted, error: insErr } = await supabase.from("waitlist_entries").insert([{
        client_id: client.id,
        client_name: client.name,
        client_phone: client.phone,
        client_email: client.email,
        service_id: svc?.id || null,
        treatment_name: svc?.name || null,
        provider_id: prov?.id || null,
        provider_name: prov?.name || null,
        target_date: targetDate,
        target_time: targetTime || null,
        notes: notes || null,
        status: "waiting",
        created_by: user?.id || null,
        created_by_name: prof?.full_name || user?.email || null,
        auto_promote: true,
      }]).select().single();
      if (insErr) throw insErr;

      // Auto-send "you're on waitlist" SMS — fire-and-forget so save doesn't block
      try {
        const firstName = (client.name || "").split(" ")[0];
        const treatment = svc?.name || "your treatment";
        const dateStr = fmtDate(targetDate);
        const timeStr = targetTime ? ` at ${fmt12(targetTime)}` : "";
        const provStr = prov?.name ? ` with ${prov.name.split(" ")[0]}` : "";
        const message =
`Hi ${firstName}! 💛

You're officially on Nova Med Spa's waitlist for:
✨ ${treatment}${provStr}
📅 ${dateStr}${timeStr}

If a spot opens up, we'll text you a confirmation right away. No need to do anything — we've got you. 💛

— Nova Med Spa`;
        await supabase.functions.invoke("send-sms", {
          body: { to: client.phone, message, sender: "system", source: "waitlist_added" }
        });
        await supabase.from("waitlist_entries").update({
          notified_at: new Date().toISOString(),
        }).eq("id", inserted.id);
      } catch (e) {
        console.warn("Waitlist welcome SMS failed:", e);
      }

      onSaved();
    } catch (e) {
      alert("Save failed: " + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1200,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:520,maxHeight:"92vh",overflow:"auto",padding:22,position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:10,right:12,background:C.light,border:"none",borderRadius:6,padding:"3px 9px",cursor:"pointer",fontSize:18,color:C.muted}}>×</button>

        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>⏳ Add to Waitlist</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:16}}>SMS will be sent automatically to confirm they're on the list.</div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Client picker */}
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase"}}>Client *</label>
              <button onClick={()=>{setUseNewClient(!useNewClient);setSelectedClient(null);setClientResults([]);}} style={{fontSize:10,color:C.gold,background:"none",border:"none",cursor:"pointer",fontFamily:F,textDecoration:"underline"}}>
                {useNewClient ? "← Use existing" : "+ New client"}
              </button>
            </div>

            {!useNewClient && !selectedClient && (
              <>
                <input value={clientSearch} onChange={e=>setClientSearch(e.target.value)} placeholder="🔍 Search name, phone, or email..." style={inp}/>
                {searching && <div style={{padding:"6px 4px",fontSize:11,color:C.muted}}>Searching...</div>}
                {clientResults.length > 0 && (
                  <div style={{marginTop:6,border:`1px solid ${C.border}`,borderRadius:8,maxHeight:200,overflowY:"auto"}}>
                    {clientResults.map(c => (
                      <div key={c.id} onClick={()=>{setSelectedClient(c);setClientResults([]);setClientSearch("");}} style={{padding:"8px 12px",cursor:"pointer",borderBottom:`1px solid ${C.border}`,fontSize:12,color:C.text}} onMouseEnter={e=>e.currentTarget.style.background=C.light} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{fontWeight:700}}>{c.name}</div>
                        <div style={{fontSize:10,color:C.muted}}>{c.phone}{c.email ? " · "+c.email : ""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {!useNewClient && selectedClient && (
              <div style={{padding:"10px 12px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{selectedClient.name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{selectedClient.phone}</div>
                </div>
                <button onClick={()=>setSelectedClient(null)} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:14}}>×</button>
              </div>
            )}

            {useNewClient && (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <input value={newClient.name} onChange={e=>setNewClient({...newClient,name:e.target.value})} placeholder="Full name" style={inp}/>
                <input value={newClient.phone} onChange={e=>setNewClient({...newClient,phone:e.target.value})} placeholder="Phone (e.g. 555-123-4567)" style={inp}/>
                <input value={newClient.email} onChange={e=>setNewClient({...newClient,email:e.target.value})} placeholder="Email (optional)" style={inp}/>
              </div>
            )}
          </div>

          {/* Treatment + provider */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Treatment</label>
              <select value={serviceId} onChange={e=>setServiceId(e.target.value)} style={inp}>
                <option value="">Any service</option>
                {services.filter(s => s.active !== false).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Provider</label>
              <select value={providerId} onChange={e=>setProviderId(e.target.value)} style={inp}>
                <option value="">Any provider</option>
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date + time */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Target date *</label>
              <input type="date" value={targetDate} onChange={e=>setTargetDate(e.target.value)} style={inp}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Target time</label>
              <input type="time" value={targetTime} onChange={e=>setTargetTime(e.target.value)} style={inp}/>
            </div>
          </div>

          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Notes (optional)</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2} placeholder="e.g. flexible mornings, prefer Sarah" style={{...inp,resize:"vertical"}}/>
          </div>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
          <button onClick={onClose} disabled={busy} style={{padding:"10px 16px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={save} disabled={!canSave || busy} style={{padding:"10px 22px",background:canSave?`linear-gradient(135deg,${C.gold},${C.goldD})`:C.light,color:canSave?"#fff":C.muted,border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:canSave?"pointer":"not-allowed",fontFamily:F}}>
            {busy ? "Adding..." : "Add to Waitlist + Send SMS"}
          </button>
        </div>
      </div>
    </div>
  );
}
